import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

// const API_BASE = "http://localhost:9090/api";
const API_BASE = "https://node-api.mediitacademy.com/api";

type PlanDetails = {
  id: string;
  name: string;
  price: string | number;
  duration: string;
  features: string[];
};

type BatchOption = {
  id: number;
  name: string;
  courseId: number;
  courseTitle?: string;
  teacher?: string | null;
  startDate: string;
  startTime: string;
  startAt?: string;
  days: string;
  seats: number;
  enrolledStudents: number;
  status: string;
};

const getStoredStudentEmail = () => {
  const directEmail = localStorage.getItem("email");
  if (directEmail) return directEmail;

  try {
    const student = JSON.parse(localStorage.getItem("student") || "{}");
    return student?.email || "";
  } catch {
    return "";
  }
};

const formatBatchDateTime = (batch: BatchOption | null) => {
  if (!batch) return "Schedule to be announced";
  const raw =
    batch.startAt || `${batch.startDate}T${String(batch.startTime).slice(0, 5)}`;
  const date = new Date(raw);

  if (Number.isNaN(date.getTime())) {
    return `${batch.startDate} ${String(batch.startTime || "").slice(0, 5)}`;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPrice = (price: string | number | undefined) => {
  if (price === undefined || price === null || price === "") return "0";
  return String(price).replace(/[^\d.]/g, "") || String(price);
};

const CoursePurchase: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();

  const [selectedPlan, setSelectedPlan] = useState<string>("Pro");
  const [selectedBatchId, setSelectedBatchId] = useState<string>("");
  const [courseData, setCourseData] = useState<any>(null);
  const [batches, setBatches] = useState<BatchOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [batchLoading, setBatchLoading] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const plansList: PlanDetails[] = Array.isArray(courseData?.plans)
    ? courseData.plans
    : [];

  const featuresList: string[] = Array.isArray(courseData?.features)
    ? courseData.features
    : [];

  const selectedPlanData = useMemo(
    () =>
      plansList.find((plan) => plan.id === selectedPlan) ||
      plansList.find((plan) => plan.id === "Pro") ||
      plansList[0] ||
      null,
    [plansList, selectedPlan]
  );

  const selectedBatch = useMemo(
    () => batches.find((batch) => String(batch.id) === selectedBatchId) || null,
    [batches, selectedBatchId]
  );

  // Fallback calculations for UI display
  const currentPrice = selectedPlanData?.price || courseData?.price || 0;
  const currentPlanName = selectedPlanData?.name || "Full Course";
  const currentBatchName = selectedBatch ? selectedBatch.name : "Upcoming Batch";

  const fetchCourseDetails = async () => {
    if (!courseId) return;

    const response = await fetch(`${API_BASE}/courses/${courseId}`);
    if (!response.ok) throw new Error("Course not found");

    const data = await response.json();
    setCourseData(data);

    if (Array.isArray(data.plans) && data.plans.length > 0) {
      const defaultPlan =
        data.plans.find((plan: PlanDetails) => plan.id === "Pro") ||
        data.plans[0];
      setSelectedPlan(defaultPlan.id);
    }
  };

  const fetchAvailableBatches = async () => {
    if (!courseId) return;

    setBatchLoading(true);

    try {
      const response = await fetch(`${API_BASE}/batches/available/${courseId}`);
      const data = await response.json();
      const incoming = Array.isArray(data) ? data : [];

      setBatches(incoming);

      if (incoming.length > 0) {
        setSelectedBatchId(String(incoming[0].id));
      } else {
        setSelectedBatchId("");
      }
    } catch (error) {
      console.error("Available batches error:", error);
      setBatches([]);
      setSelectedBatchId("");
    } finally {
      setBatchLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        await Promise.all([fetchCourseDetails(), fetchAvailableBatches()]);
      } catch (error) {
        console.error("Course purchase load error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [courseId]);

  const handleOpenPaymentModal = () => {
    const email = getStoredStudentEmail();

    if (!email) {
      alert("Please login first to continue with enrollment.");
      window.location.href = "/login";
      return;
    }

    setShowPaymentModal(true);
  };

  const handleManualSubmit = async () => {
    if (!transactionId.trim()) {
      alert("Please enter the Transaction ID / UTR Number or Card details.");
      return;
    }

    const email = getStoredStudentEmail();

    if (!email) {
      alert("Login session not found. Please login again.");
      window.location.href = "/login";
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/enrollments/manual-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          courseId: Number(courseId),
          batchId: selectedBatch?.id || 0, // Fallback to 0 if no batch
          amount: currentPrice,
          planName: currentPlanName,
          transactionId: transactionId.trim(),
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.error || data?.message || "Failed to submit payment."
        );
      }

      alert("Payment details submitted! Admin verify karega, uske baad course unlock ho jayega.");

      setShowPaymentModal(false);
      window.location.href = "/student-dashboard/profile";
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Server error. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <CourseLoading />;
  }

  if (!courseData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f8fbff] text-slate-950 font-['Inter']">
        <div className="rounded-[34px] bg-white p-10 text-center shadow-sm">
          <h2 className="text-2xl font-black">Course Not Found!</h2>
          <button
            onClick={() => window.history.back()}
            className="mt-5 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // const imageUrl = courseData.image_url
  //   ? `http://localhost:9090${courseData.image_url}`
  //   : "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=1200&auto=format&fit=crop";
  const imageUrl = courseData.image_url
    ? `https://node-api.mediitacademy.com${courseData.image_url}`
    : "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=1200&auto=format&fit=crop";

  const courseHighlights = [
    {
      icon: "🎥",
      title: "Live + Recorded Classes",
      desc: "Batch ke saath live class aur revision ke liye recordings.",
    },
    {
      icon: "📘",
      title: "PDF Notes & Materials",
      desc: "Chapter-wise notes, assignments aur practice material.",
    },
    {
      icon: "🧪",
      title: "Mock Tests",
      desc: "Exam pattern based test series aur performance tracking.",
    },
    {
      icon: "👨‍🏫",
      title: "Mentor Support",
      desc: "Teacher guidance aur doubts ke liye support system.",
    },
  ];

  const courseDetails = [
    { label: "Category", value: courseData.category || "Course" },
    { label: "Mentor", value: courseData.teacher || "Assigned Teacher" },
    { label: "Duration", value: selectedPlanData?.duration || courseData.duration || "6 Months" },
    { label: "Mode", value: "Online Live + Recorded" },
    { label: "Language", value: "Hindi / English Mix" },
    { label: "Access", value: "Notes, Tests, Live Classes" },
  ];

  const syllabusList =
    featuresList.length > 0
      ? featuresList.slice(0, 6)
      : [
          "Recorded video lectures",
          "Chapter-wise PDF notes",
          "Weekly mock tests",
          "Live doubt support",
          "Assignments and practice sheets",
          "Progress tracking dashboard",
        ];

  return (
    <div className="relative min-h-screen bg-[#edf4ff] pb-28 text-[#101827] font-['Inter']">
      <style>{`
        ::selection {
          background: #123b7a;
          color: #ffffff;
        }
      `}</style>

      {showPaymentModal && (
        <PaymentModal
          price={currentPrice}
          planName={currentPlanName}
          batchName={currentBatchName}
          batch={selectedBatch}
          transactionId={transactionId}
          setTransactionId={setTransactionId}
          isSubmitting={isSubmitting}
          closeModal={() => setShowPaymentModal(false)}
          handleManualSubmit={handleManualSubmit}
        />
      )}

      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/95 px-4 py-3 shadow-sm backdrop-blur-xl sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-[1420px] items-center justify-between gap-4">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 rounded-2xl bg-blue-50 px-4 py-3 text-sm font-black text-blue-700 transition hover:bg-blue-100"
          >
            ← Back
          </button>

          <div className="hidden text-center sm:block">
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">
              Course Subscription
            </p>
            <h1 className="text-lg font-black text-slate-950">
              {courseData.title}
            </h1>
          </div>

          <button
            onClick={handleOpenPaymentModal}
            className="rounded-2xl bg-[#123b7a] px-5 py-3 text-sm font-black text-white transition hover:bg-[#0a2550] shadow-md"
          >
            Enroll Now
          </button>
        </div>
      </header>

      <section className="relative overflow-hidden bg-gradient-to-br from-[#061226] via-[#08234a] to-[#123b7a] pb-20 pt-8 text-white lg:pb-28">
        <div className="absolute -left-28 -top-28 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-white/5 blur-3xl" />

        <div className="relative mx-auto max-w-[1420px] px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1fr_520px] lg:items-center">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 backdrop-blur">
                <span className="h-2.5 w-2.5 rounded-full bg-blue-300" />
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-100">
                  {courseData.category || "Premium Course"}
                </p>
              </div>

              <h2 className="max-w-4xl text-4xl font-black leading-tight tracking-[-0.05em] sm:text-5xl lg:text-6xl">
                {courseData.title}
              </h2>

              <p className="mt-5 max-w-2xl text-base font-semibold leading-8 text-blue-100/85 sm:text-lg">
                {courseData.description ||
                  "Is course me aapko structured live classes, recordings, notes, mock tests aur mentor guidance milegi, jisse preparation ekdum organized rahegi."}
              </p>

              <div className="mt-7 grid max-w-3xl gap-3 sm:grid-cols-3">
                <HeroMini label="Mentor" value={courseData.teacher || "Assigned"} />
                <HeroMini
                  label="Starting Price"
                  value={`₹${formatPrice(currentPrice)}`}
                />
                <HeroMini
                  label="Batches"
                  value={batches.length > 0 ? `${batches.length} Open` : "Upcoming"}
                />
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={handleOpenPaymentModal}
                  className="rounded-2xl bg-white px-6 py-4 text-sm font-black text-[#123b7a] transition hover:-translate-y-1 hover:shadow-xl"
                >
                  Start Subscription
                </button>

                <a
                  href="#course-details"
                  className="rounded-2xl border border-white/20 bg-white/10 px-6 py-4 text-sm font-black text-white backdrop-blur transition hover:bg-white/15"
                >
                  View Course Details
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-5 rounded-[42px] bg-blue-400/20 blur-3xl" />
              <div className="relative overflow-hidden rounded-[38px] border border-white/10 bg-white/10 p-3 shadow-2xl backdrop-blur">
                <div className="relative aspect-video overflow-hidden rounded-[30px] bg-slate-900">
                  <img
                    src={imageUrl}
                    alt={courseData.title || "Course preview"}
                    className="h-full w-full object-cover opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />

                  {courseData.video_url && (
                    <a
                      href={courseData.video_url}
                      target="_blank"
                      rel="noreferrer"
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <span className="flex h-20 w-20 items-center justify-center rounded-full border border-white/30 bg-white/20 text-white shadow-xl backdrop-blur-md transition hover:scale-110 hover:bg-blue-600">
                        ▶
                      </span>
                    </a>
                  )}

                  <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/12 p-4 backdrop-blur">
                    <p className="text-xs font-black uppercase tracking-wide text-blue-100">
                      Course Preview
                    </p>
                    <p className="mt-1 text-sm font-black text-white">
                      {courseData.title} • {courseData.teacher || "Mentor"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="relative z-10 mx-auto -mt-12 max-w-[1420px] space-y-10 px-4 sm:px-6 lg:px-8">
        <section id="course-details" className="grid gap-6 xl:grid-cols-[1fr_420px]">
          <div className="rounded-[40px] bg-white p-6 shadow-lg shadow-slate-200/60 sm:p-8">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
              Course Full Details
            </p>
            <h2 className="mt-2 text-3xl font-black tracking-[-0.04em] text-slate-950">
              What you will get in this subscription
            </h2>
            <p className="mt-3 max-w-4xl text-sm font-semibold leading-7 text-slate-500">
              Is page par student ko course ke complete details, included
              features, subscription plans, upcoming batches aur payment
              verification flow clear tarike se dikhega.
            </p>

            <div className="mt-7 grid gap-4 md:grid-cols-2">
              {courseDetails.map((item) => (
                <div
                  key={item.label}
                  className="rounded-[24px] bg-[#f6f9ff] p-5"
                >
                  <p className="text-xs font-black uppercase tracking-wide text-slate-400">
                    {item.label}
                  </p>
                  <p className="mt-2 text-base font-black text-slate-950">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {courseHighlights.map((item) => (
                <div
                  key={item.title}
                  className="rounded-[28px] border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-5 transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-2xl shadow-sm">
                    {item.icon}
                  </div>
                  <h3 className="mt-4 text-lg font-black text-slate-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <aside className="rounded-[40px] bg-white p-6 shadow-lg shadow-slate-200/60">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
              Quick Checkout
            </p>

            <div className="mt-5 rounded-[30px] bg-gradient-to-br from-[#061226] via-[#08234a] to-[#123b7a] p-6 text-white">
              <p className="text-sm font-bold text-blue-100">Selected Plan</p>
              <h3 className="mt-2 text-3xl font-black">
                ₹{formatPrice(currentPrice)}
              </h3>
              <p className="mt-1 text-sm font-bold text-blue-100">
                {currentPlanName}
              </p>

              <div className="mt-5 rounded-2xl bg-white/10 p-4">
                <p className="text-xs font-black uppercase tracking-wide text-blue-100">
                  Selected Batch
                </p>
                <p className="mt-1 text-sm font-black">
                  {currentBatchName}
                </p>
                <p className="mt-1 text-xs font-bold text-blue-100">
                  {formatBatchDateTime(selectedBatch)}
                </p>
              </div>

              <button
                onClick={handleOpenPaymentModal}
                className="mt-5 w-full rounded-2xl bg-white px-5 py-4 text-sm font-black text-[#123b7a] transition hover:-translate-y-1 shadow-md"
              >
                Proceed to Checkout →
              </button>
            </div>

            <div className="mt-5 space-y-3">
              <CheckoutPoint text="Card and UPI payment options" />
              <CheckoutPoint text="Batch selection saved with enrollment" />
              <CheckoutPoint text="Instant access after verification" />
            </div>
          </aside>
        </section>

        <section className="rounded-[40px] bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                Program Structure
              </p>
              <h2 className="mt-2 text-3xl font-black tracking-[-0.04em] text-slate-950">
                Course Syllabus & Learning Path
              </h2>
              <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
                Student ko subscription se pehle clear ho jayega ki course me
                kya-kya milega.
              </p>
            </div>

            <span className="w-fit rounded-full bg-blue-50 px-4 py-2 text-xs font-black text-blue-700">
              {syllabusList.length} Modules
            </span>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {syllabusList.map((item, index) => (
              <div
                key={`${item}-${index}`}
                className="rounded-[28px] border border-slate-100 bg-[#f8fbff] p-5"
              >
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-sm font-black text-white">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div>
                    <h3 className="text-base font-black text-slate-950">
                      {item}
                    </h3>
                    <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
                      Concept explanation, practice aur revision support
                      included.
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="mb-8 text-center">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
              Select Your Plan
            </p>
            <h2 className="mt-2 text-4xl font-black tracking-[-0.05em] text-slate-950">
              Choose the perfect subscription
            </h2>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              Plan choose karne ke baad batch select karke payment verification
              submit karein.
            </p>
          </div>

          {plansList.length > 0 ? (
            <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-3">
              {plansList.map((plan: PlanDetails) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  selected={selectedPlan === plan.id}
                  isPopular={plan.id === "Pro"}
                  onClick={() => setSelectedPlan(plan.id)}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-[34px] bg-white p-8 text-center text-sm font-bold text-slate-500 shadow-sm">
              Standard base plan applied by default.
            </div>
          )}
        </section>

        <section className="rounded-[40px] border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50 sm:p-8">
          <div className="mb-6 flex flex-col justify-between gap-3 md:flex-row md:items-end">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                Select Batch
              </p>
              <h2 className="mt-2 text-3xl font-black tracking-[-0.04em] text-slate-950">
                Choose your upcoming batch
              </h2>
              <p className="mt-2 max-w-4xl text-sm font-bold leading-6 text-slate-500">
                Jo batch start ho chuka hai wo yahan nahi dikhega. Example: 1
                July batch start ho gaya to 2 July se student ko next upcoming
                batch dikhega.
              </p>
            </div>

            <button
              onClick={fetchAvailableBatches}
              className="w-fit rounded-2xl bg-slate-100 px-4 py-2.5 text-xs font-black text-slate-700 hover:bg-slate-200"
            >
              Refresh Batches
            </button>
          </div>

          {batchLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50 p-8 text-center text-sm font-black text-slate-500">
              Loading batches...
            </div>
          ) : batches.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-orange-200 bg-orange-50 p-8 text-center">
              <h3 className="text-lg font-black text-orange-700">
                No upcoming batch available
              </h3>
              <p className="mt-2 text-sm font-bold text-orange-600">
                You can still proceed to payment. Admin will assign you to the next available batch automatically.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {batches.map((batch) => (
                <BatchCard
                  key={batch.id}
                  batch={batch}
                  selected={selectedBatchId === String(batch.id)}
                  onClick={() => setSelectedBatchId(String(batch.id))}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <section className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-12px_40px_rgba(15,23,42,0.08)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1420px] flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-slate-400">
              Total Amount Payable
            </p>
            <p className="text-2xl font-black text-slate-950">
              ₹{formatPrice(currentPrice)}
              <span className="ml-2 text-sm font-bold text-slate-500">
                ({currentPlanName})
              </span>
            </p>
            <p className="mt-1 text-sm font-bold text-blue-700">
              Batch: {currentBatchName}
            </p>
          </div>

          <button
            onClick={handleOpenPaymentModal}
            className="rounded-2xl bg-[#123b7a] px-10 py-4 text-base font-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-[#0a2550] hover:shadow-xl"
          >
            Proceed to Checkout →
          </button>
        </div>
      </section>
    </div>
  );
};

const CourseLoading = () => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
    </div>
  );
};

const PaymentModal = ({
  price,
  planName,
  batchName,
  batch,
  transactionId,
  setTransactionId,
  isSubmitting,
  closeModal,
  handleManualSubmit,
}: {
  price: string | number;
  planName: string;
  batchName: string;
  batch: BatchOption | null;
  transactionId: string;
  setTransactionId: (value: string) => void;
  isSubmitting: boolean;
  closeModal: () => void;
  handleManualSubmit: () => void;
}) => {
  const [method, setMethod] = useState<"UPI" | "CARD">("UPI");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/65 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative flex w-full max-w-[420px] max-h-[90vh] flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
        
        {/* Compact Header Section */}
        <div className="shrink-0 bg-gradient-to-br from-[#061226] via-[#08234a] to-[#123b7a] p-5 text-white">
          <button
            onClick={closeModal}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-sm font-black text-white transition hover:bg-red-500"
          >
            ×
          </button>

          <h2 className="text-xl font-black">Complete Payment</h2>
          <div className="mt-1.5 flex items-center justify-between">
            <p className="text-xs font-bold text-blue-100">
              Amount: <span className="text-lg font-black text-white">₹{formatPrice(price)}</span>
            </p>
          </div>

          <div className="mt-3 rounded-xl border border-white/10 bg-white/10 p-2.5">
            <p className="text-[10px] font-black uppercase tracking-wide text-blue-100">
              Selected Details
            </p>
            <p className="text-xs font-bold text-white truncate">
              {planName} • {batchName}
            </p>
          </div>
        </div>

        {/* Scrollable Body Section */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => setMethod("UPI")}
              className={`flex-1 rounded-xl border py-2.5 text-xs font-black transition ${
                method === "UPI" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"
              }`}
            >
              UPI App
            </button>
            <button
              onClick={() => setMethod("CARD")}
              className={`flex-1 rounded-xl border py-2.5 text-xs font-black transition ${
                method === "CARD" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"
              }`}
            >
              Credit/Debit Card
            </button>
          </div>

          {method === "UPI" ? (
            <div className="animate-in fade-in duration-300">
              <div className="mb-4 flex justify-center">
                <div className="flex h-28 w-28 items-center justify-center rounded-[20px] border-4 border-blue-50 bg-white p-2 shadow-sm">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg"
                    alt="QR Code"
                    className="h-full w-full object-contain opacity-85"
                  />
                </div>
              </div>
              
              <div className="mb-4 rounded-xl border border-slate-100 bg-slate-50 p-3 text-center">
                <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Official UPI ID
                </p>
                <p className="text-sm font-black text-slate-900">
                  mediitacademy@okaxis
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-[10px] font-black uppercase tracking-wide text-slate-500">
                  Transaction ID / UTR No.
                </label>
                <input
                  type="text"
                  value={transactionId}
                  onChange={(event) => setTransactionId(event.target.value)}
                  placeholder="e.g. 123456789012"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-700 outline-none transition focus:border-blue-500 focus:bg-white"
                />
              </div>
            </div>
          ) : (
            <div className="animate-in fade-in duration-300 space-y-3">
               <div>
                <label className="mb-1.5 block text-[10px] font-black uppercase text-slate-500">Card Number</label>
                <input
                  type="text"
                  placeholder="4111 1111 1111 1111"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold tracking-widest outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex gap-3">
                 <div className="flex-1">
                  <label className="mb-1.5 block text-[10px] font-black uppercase text-slate-500">Expiry</label>
                  <input placeholder="MM/YY" className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold outline-none focus:border-blue-500" />
                </div>
                <div className="flex-1">
                  <label className="mb-1.5 block text-[10px] font-black uppercase text-slate-500">CVV</label>
                  <input type="password" placeholder="•••" className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold tracking-widest outline-none focus:border-blue-500" />
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleManualSubmit}
            disabled={isSubmitting}
            className={`mt-5 w-full rounded-xl py-3.5 text-sm font-black text-white transition shadow-md ${
              isSubmitting
                ? "cursor-not-allowed bg-slate-400"
                : "bg-[#123b7a] hover:-translate-y-0.5 hover:bg-[#0a2550] hover:shadow-lg"
            }`}
          >
            {isSubmitting ? "Processing..." : `Pay ₹${formatPrice(price)}`}
          </button>

          <p className="mt-3 text-center text-[10px] font-bold leading-relaxed text-slate-400">
            Aapki payment details verify hone ke baad course turant unlock ho jayega.
          </p>
        </div>
      </div>
    </div>
  );
};

const HeroMini = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="rounded-[22px] border border-white/10 bg-white/10 p-4 backdrop-blur">
      <p className="text-xs font-black uppercase tracking-wide text-blue-100">
        {label}
      </p>
      <p className="mt-1 truncate text-lg font-black text-white">{value}</p>
    </div>
  );
};

const CheckoutPoint = ({ text }: { text: string }) => {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-[#f6f9ff] p-4">
      <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50 text-sm font-black text-blue-700">
        ✓
      </span>
      <p className="text-sm font-bold text-slate-600">{text}</p>
    </div>
  );
};

const PlanCard = ({
  plan,
  selected,
  isPopular,
  onClick,
}: {
  plan: PlanDetails;
  selected: boolean;
  isPopular: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-full cursor-pointer rounded-[36px] p-8 text-left transition-all duration-300 ${
        selected
          ? isPopular
            ? "scale-[1.02] border-2 border-blue-500 bg-[#101827] text-white shadow-2xl"
            : "scale-[1.02] border-2 border-blue-600 bg-white text-slate-950 shadow-2xl"
          : "border border-slate-200 bg-white text-slate-950 hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl"
      }`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-4 py-1.5 text-xs font-black uppercase tracking-wider text-white shadow-lg">
          Most Recommended
        </div>
      )}

      <h3
        className={`text-xl font-black ${
          selected && isPopular ? "text-blue-400" : "text-blue-600"
        }`}
      >
        {plan.name}
      </h3>

      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-4xl font-black">₹{formatPrice(plan.price)}</span>
        <span
          className={`text-sm font-bold ${
            selected && isPopular ? "text-white/60" : "text-slate-500"
          }`}
        >
          / {plan.duration}
        </span>
      </div>

      <div
        className={`mt-6 h-px w-full ${
          selected && isPopular ? "bg-white/10" : "bg-slate-100"
        }`}
      />

      <ul className="mt-8 space-y-4">
        {(plan.features || []).map((feature: string, index: number) => (
          <li key={`${feature}-${index}`} className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-black ${
                selected && isPopular
                  ? "bg-blue-500 text-white"
                  : "bg-emerald-50 text-emerald-600"
              }`}
            >
              ✓
            </span>
            <span
              className={`text-sm font-semibold leading-relaxed ${
                selected && isPopular ? "text-white/90" : "text-slate-700"
              }`}
            >
              {feature}
            </span>
          </li>
        ))}
      </ul>

      <div
        className={`mt-8 flex items-center justify-center gap-2 rounded-2xl border p-4 ${
          selected ? "border-blue-500" : "border-slate-300"
        }`}
      >
        {selected && <div className="h-2.5 w-2.5 rounded-full bg-blue-500" />}
        <span
          className={`text-sm font-black ${
            selected && isPopular ? "text-white" : "text-slate-950"
          }`}
        >
          {selected ? "Selected" : "Select Plan"}
        </span>
      </div>
    </button>
  );
};

const BatchCard = ({
  batch,
  selected,
  onClick,
}: {
  batch: BatchOption;
  selected: boolean;
  onClick: () => void;
}) => {
  const remainingSeats = Math.max(
    0,
    Number(batch.seats || 0) - Number(batch.enrolledStudents || 0)
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-[28px] border p-5 text-left transition-all ${
        selected
          ? "border-blue-600 bg-blue-50 shadow-lg shadow-blue-100"
          : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-black text-slate-950">{batch.name}</h3>
          <p className="mt-1 text-xs font-bold text-slate-500">
            {batch.teacher
              ? `Teacher: ${batch.teacher}`
              : "Teacher will be assigned"}
          </p>
        </div>

        <span
          className={`rounded-full px-3 py-1 text-xs font-black ${
            selected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
          }`}
        >
          {selected ? "Selected" : "Select"}
        </span>
      </div>

      <div className="mt-4 rounded-2xl bg-white p-3">
        <p className="text-xs font-black uppercase tracking-wide text-slate-400">
          Start Date & Time
        </p>
        <p className="mt-1 text-sm font-black text-slate-900">
          {formatBatchDateTime(batch)}
        </p>
        <p className="mt-1 text-xs font-bold text-slate-500">{batch.days}</p>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs font-black text-slate-500">
        <span>Available Seats</span>
        <span>
          {remainingSeats}/{batch.seats}
        </span>
      </div>
    </button>
  );
};

export default CoursePurchase;